import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { getAuth } from 'firebase/auth';
import { addDoc, collection, doc, getDoc, getFirestore, Timestamp } from 'firebase/firestore';
import mime from 'mime';
import moment from 'moment';
import { Role } from 'src/app/enums';
import { ThemeColors, User } from 'src/app/interfaces';
import tinycolor from 'tinycolor2';
import { dataStore, dataStoreSignal, db } from './app.component';
import * as ExcelJS from 'exceljs';

export interface Color {
  name: string;
  hex: string;
  darkContrast: boolean;
}

export async function saveOrgInfoLocal(orgId: string) {
  if (!dataStore.publicSettings) {
    dataStore.publicSettings = (
      await getDoc(doc(db, `organisations/${orgId}/settings/public`))
    ).data();
  }

  if (!dataStore.selectedOrganisation) {
    dataStore.selectedOrganisation = {
      id: orgId,
      name: dataStore.publicSettings.name,
    };
  }
  dataStore.theming = {
    primaryColor: dataStore.publicSettings.primaryColor ?? '#484b99',
    accentColor: dataStore.publicSettings.accentColor ?? '#42af5a',
    banner: dataStore.publicSettings.banner ?? '../assets/default-images/default-banner.png',
    logo: dataStore.publicSettings.logo ?? '../assets/default-images/default-logo.png',
    name: dataStore.publicSettings.name ?? 'Buurtvervoer',
    favicon: dataStore.publicSettings.favicon ?? '../assets/default-images/default-favicon.ico',
    loginImage:
      dataStore.publicSettings.loginImage ?? '../assets/default-images/default-login-image.svg',
  };

  updateTheme({
    primary: dataStore.theming.primaryColor,
    secondary: dataStore.theming.accentColor,
  });
}

export function getFileExtension(file: File) {
  const fname = file.name;
  return fname.slice(((fname.lastIndexOf('.') - 1) >>> 0) + 2);
}

export async function getFileFromUrl(url: string | URL, fileName: string) {
  const faviconBlob = await (await fetch(new URL(url))).blob();

  return new File([faviconBlob], `${fileName}.${mime.getExtension(faviconBlob.type)}`, {
    type: faviconBlob.type,
  });
}

export async function logExport(exportType: string) {
  const user = getAuth().currentUser!;
  const organisationId = localStorage.getItem('orgId') as string;
  await addDoc(collection(getFirestore(), `eventLogs/${organisationId}/exportedFiles`), {
    date: new Date(),
    doneByUser: user.email,
    doneByUid: user.uid,
    exportType,
  });
}

export async function logSettingsChange(
  originalSettings: any,
  newSettings: any,
  originalPublicSettings: any,
  newPublicSettings: any,
) {
  const user = getAuth().currentUser!;
  const organisationId = localStorage.getItem('orgId') as string;
  for (const key of Object.keys(newSettings)) {
    if (newSettings[key]._methodName === 'deleteField') {
      newSettings[key] = 'DELETED';
    }
  }
  for (const key of Object.keys(newPublicSettings)) {
    if (newPublicSettings[key]._methodName === 'deleteField') {
      newPublicSettings[key] = 'DELETED';
    }
  }
  const changes: any[] = getDifferences(newSettings, originalSettings);
  const publicChanges: any[] = getDifferences(newPublicSettings, originalPublicSettings);
  if (changes.length > 0 || publicChanges.length > 0) {
    const changeObj = {
      date: new Date(),
      doneByUser: user.email,
      doneByUid: user.uid,
      changes,
      publicChanges,
    };
    console.log('Changed settings', changeObj);
    await addDoc(collection(db, `eventLogs/${organisationId}/changedSettings`), changeObj);
  }
}

function getDifferences(newObj: object, oldObj: object) {
  const changes = [];
  for (const [key, value] of Object.entries(newObj)) {
    let oldValue = oldObj[key];
    if (oldValue === undefined && value === 'DELETED') {
      continue;
    }
    if (value instanceof Date && oldValue) {
      oldValue = oldValue.toDate();
    }
    if (typeof value === 'object' && value !== null) {
      if (!objectEquals(value, oldValue)) {
        changes.push({
          from: oldValue ?? 'undefined',
          to: value ?? 'undefined',
          field: key,
        } as any);
      }
    } else if (oldValue != value) {
      changes.push({
        from: oldValue ?? 'undefined',
        to: value ?? 'undefined',
        field: key,
      } as any);
    }
  }
  return changes;
}

function objectEquals(x: object, y: object) {
  if (x === y) return true;
  // if both x and y are null or undefined and exactly the same

  if (!(x instanceof Object) || !(y instanceof Object)) return false;
  // if they are not strictly equal, they both need to be Objects

  if (x.constructor !== y.constructor) return false;
  // they must have the exact same prototype chain, the closest we can do is
  // test there constructor.

  for (var p in x) {
    if (!x.hasOwnProperty(p)) continue;
    // other properties were tested using x.constructor === y.constructor

    if (!y.hasOwnProperty(p)) return false;
    // allows to compare x[ p ] and y[ p ] when set to undefined

    if (x[p] === y[p]) continue;
    // if they have the same strict value or identity then they are equal

    if (typeof x[p] !== 'object') return false;
    // Numbers, Strings, Functions, Booleans must be strictly equal

    if (!objectEquals(x[p], y[p])) return false;
    // Objects and Arrays must be tested recursively
  }

  for (p in y) if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) return false;
  // allows x[ p ] to be set to undefined

  return true;
}

export function getDateString(date: Date) {
  return moment(date).format('YYYY-MM-DD');
}

export function openSnackbar(snackBar: MatSnackBar, text: string) {
  snackBar.open(text, 'X', {
    duration: 5000,
  });
}

export async function redirectIfNotSuperUser(router: Router) {
  const currentUserId = getAuth().currentUser?.uid;
  const currentUser = (await getDoc(doc(db, `/users/${currentUserId}`))).data() as User;
  if (!currentUser.rights || currentUser.rights !== 'admin') {
    router.navigateByUrl('/plannen');
  }
}

export async function redirectIfNotAdmin(router: Router) {
  const currentUserId = getAuth().currentUser?.uid;
  const organisationId = localStorage.getItem('orgId');

  // Check superuser rights first
  const rootUser = (await getDoc(doc(db, `/users/${currentUserId}`))).data() as User;
  if (rootUser?.rights === 'admin') {
    return;
  }

  const currentUser = (
    await getDoc(doc(db, `/organisations/${organisationId}/users/${currentUserId}`))
  ).data() as User;

  const roles = currentUser?.roles ?? [];

  if (!roles.includes(Role.Administrator)) {
    if (roles.includes(Role.Planner)) {
      router.navigateByUrl('/plannen');
    } else if (roles.includes(Role.Driver)) {
      router.navigateByUrl(`/schedule/driver/${currentUserId}`);
    } else if (roles.includes(Role.CoDriver)) {
      router.navigateByUrl(`/schedule/co-driver/${currentUserId}`);
    } else if (roles.includes(Role.Other)) {
      router.navigateByUrl('/account');
    }
  }
}

export async function redirectIfDriver(router: Router) {
  const currentUserId = getAuth().currentUser?.uid;
  const organisationId = localStorage.getItem('orgId');

  // Check superuser rights first
  const rootUser = (await getDoc(doc(db, `/users/${currentUserId}`))).data() as User;
  if (rootUser?.rights === 'admin') {
    return;
  }

  const currentUser = (
    await getDoc(doc(db, `/organisations/${organisationId}/users/${currentUserId}`))
  ).data() as User;

  const roles = currentUser?.roles ?? [];

  if (
    (roles.includes(Role.Driver) || roles.includes(Role.CoDriver)) &&
    !roles.includes(Role.Planner) &&
    !roles.includes(Role.RidePlanner) &&
    !roles.includes(Role.Administrator)
  ) {
    if (roles.includes(Role.Driver)) {
      router.navigateByUrl(`/schedule/driver/${currentUserId}`);
    } else if (roles.includes(Role.CoDriver)) {
      router.navigateByUrl(`/schedule/co-driver/${currentUserId}`);
    }
  }
}

export function updateTheme(themeColors: ThemeColors) {
  const palettes = Object.keys(themeColors);
  for (const palette of palettes) {
    if (palette !== 'master') {
      // generate the shades of the palette, based on the base color of the theme palette
      const colors = computeColors(themeColors[palette]);
      // console.log(themeColors[palette]);
      // set colors on all root variables of the palette that the dynamic theme uses
      for (const color of colors) {
        document.documentElement.style.setProperty(`--${palette}-${color.name}`, color.hex);
      }
      // set the root variables for the main color and contrast color of the palette. the contrast variable is used by the dynamic theme to fix some incorrect contrast colors.
      const mainColor = colors.find((color) => color.name === '40');
      document.documentElement.style.setProperty(`--${palette}`, mainColor.hex);
      document.documentElement.style.setProperty(
        `--${palette}-contrast`,
        mainColor.darkContrast ? 'black' : 'white',
      );
    } else if (palette === 'master') {
      const mainColor = getColorObject(tinycolor(themeColors[palette]));
      document.documentElement.style.setProperty(`--${palette}`, mainColor.hex);
      document.documentElement.style.setProperty(
        `--${palette}-contrast`,
        mainColor.darkContrast ? 'black' : 'white',
      );
    }
  }
  updateRippleColors();
}
function computeColors(hex: string): Color[] {
  return [
    getColorObject(tinycolor(hex).darken(30), '10'),
    getColorObject(tinycolor(hex).darken(20), '20'),
    getColorObject(tinycolor(hex).darken(15), '25'),
    getColorObject(tinycolor(hex).darken(10), '30'),
    getColorObject(tinycolor(hex).darken(5), '35'),
    getColorObject(tinycolor(hex), '40'),
    getColorObject(tinycolor(hex).lighten(10), '50'),
    getColorObject(tinycolor(hex).lighten(20), '60'),
    getColorObject(tinycolor(hex).lighten(30), '70'),
    getColorObject(tinycolor(hex).lighten(40), '80'),
    getColorObject(tinycolor(hex).lighten(50), '90'),
    getColorObject(tinycolor(hex).lighten(55), '95'),
    getColorObject(tinycolor(hex).lighten(58), '98'),
    getColorObject(tinycolor(hex).lighten(59), '99'),
  ];
}
export function getColorObject(value, name = ''): Color {
  const c = tinycolor(value);
  return {
    name: name,
    hex: c.toHexString(),
    darkContrast: c.getLuminance() > 0.45,
  };
}

export function ucFirst(string: string) {
  return String(string).charAt(0).toUpperCase() + String(string).slice(1);
}
function updateRippleColors() {
  // update Angular ripple variables that do not have rgba when using a dynamic theme, despite needing rgba for the opacity
  const rippleVariables = [
    '--mat-fab-ripple-color',
    '--mat-fab-small-ripple-color',
    '--mat-button-protected-ripple-color',
    '--mat-button-text-ripple-color',
    '--mat-button-outlined-ripple-color',
  ];
  rippleVariables.forEach((rippleVariable) => {
    const rgba = hexToRgba(
      window.getComputedStyle(document.body).getPropertyValue(rippleVariable),
      0.12,
    );
    if (rgba) {
      document.body.style.setProperty(
        rippleVariable,
        `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`,
      );
    }
  });
}
function hexToRgba(hex, opacity) {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
        a: opacity,
      }
    : null;
}

export function isExcelFile(file: File): boolean {
  const validMimeTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ];

  const validExtensions = ['.xlsx', '.xls'];

  const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  const isValidMimeType = validMimeTypes.includes(file.type);
  const isValidExtension = validExtensions.includes(fileExtension);

  return isValidMimeType || isValidExtension;
}

export function generateRandomString(): string {
  const lowerCaseChars = 'abcdefghijklmnopqrstuvwxyz';
  const upperCaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numberChars = '0123456789';
  const specialChars = '!_-=+.$';

  const allChars = lowerCaseChars + upperCaseChars + numberChars + specialChars;
  const passwordLength = 16;
  let password = '';

  // Ensure the password contains at least one of each required character type
  password += lowerCaseChars[Math.floor(Math.random() * lowerCaseChars.length)];
  password += upperCaseChars[Math.floor(Math.random() * upperCaseChars.length)];
  password += numberChars[Math.floor(Math.random() * numberChars.length)];
  password += specialChars[Math.floor(Math.random() * specialChars.length)];

  // Fill the rest of the password length with random characters from the combined set
  for (let i = 4; i < passwordLength; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  // Shuffle the generated password to randomize the character order
  return shuffleString(password);
}

function shuffleString(str: string): string {
  const arr = str.split('');
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]]; // Swap elements
  }
  return arr.join('');
}

export function valueToDate(value: any): Date | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (value?.toDate) {
    return value.toDate();
  }
  if (value?.seconds !== undefined && value?.nanoseconds !== undefined) {
    return new Timestamp(value.seconds, value.nanoseconds).toDate();
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Translates address validation error messages from English to Dutch
 * @param error - The English error message from AddressValidationService
 * @returns Translated Dutch error message
 */
export function translateAddressError(error: string): string {
  // Translate common error messages from AddressValidationService to Dutch
  if (error.includes('No postal code found')) {
    return 'Geen geldige postcode gevonden in het adres';
  }
  if (error.includes('Invalid postal code:')) {
    const postalCode = error.match(/Invalid postal code: (.+)\./)?.[1];
    return postalCode ? `Ongeldige postcode: ${postalCode}` : 'Ongeldige postcode';
  }
  if (error.includes('No results found for this address (low confidence)')) {
    return 'Geen resultaten gevonden voor dit adres';
  }
  if (error.includes('No results found for this address')) {
    return 'Geen resultaten gevonden voor dit adres';
  }
  if (error.includes('API Error:')) {
    return 'API fout bij het valideren van het adres';
  }
  if (error.includes('Address is empty or invalid')) {
    return 'Adres is leeg of ongeldig';
  }
  if (error.includes('Validation failed:')) {
    return 'Validatie mislukt';
  }
  if (error.includes('No matching postal code found in geocoding results.')) {
    return 'Geen overeenkomende postcode gevonden bij opgegeven adres';
  }
  if (error.includes('Invalid housenumberAddition')) {
    return 'Ongeldige huisnummer toevoeging.';
  }
  // Default fallback
  return 'Onbekende validatiefout';
}

export async function exportJsonToExcel(
  data: any[],
  fileName: string,
  sheetName: string = 'Sheet1',
) {
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    if (data && data.length > 0) {
      // Extract column headers dynamically from all objects' keys to ensure all data is included
      const allKeys = new Set<string>();
      data.forEach((obj) => {
        Object.keys(obj).forEach((key) => allKeys.add(key));
      });
      const headers = Array.from(allKeys);
      worksheet.columns = headers.map((key) => ({ header: key, key: key }));
      worksheet.addRows(data);
    }

    let buffer;
    let mimeType;
    if (fileName.endsWith('.csv')) {
      buffer = await workbook.csv.writeBuffer();
      // Prepend UTF-8 BOM so Excel opens it automatically with correct encoding
      buffer = new Uint8Array([0xef, 0xbb, 0xbf, ...new Uint8Array(buffer as ArrayBuffer)]);
      mimeType = 'text/csv;charset=utf-8;';
    } else {
      buffer = await workbook.xlsx.writeBuffer();
      mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }

    const blob = new Blob([buffer], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;

    // Append to body to ensure it works in all browsers
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error in exportJsonToExcel:', error);
    throw error;
  }
}

/**
 * Reads an Excel file and returns the first worksheet as a "spreadsheet" object
 * similar to the one used in the current XLSX-based implementation.
 * @param file - The Excel file to read
 * @returns A promise that resolves to a "spreadsheet" object (map of rowId -> colId -> value)
 */
export async function readExcelFile(file: File): Promise<any> {
  const workbook = new ExcelJS.Workbook();
  const arrayBuffer = await file.arrayBuffer();
  await workbook.xlsx.load(arrayBuffer);
  const worksheet = workbook.worksheets[0];

  const spreadsheet = {};
  worksheet.eachRow((row, rowNumber) => {
    spreadsheet[rowNumber] = {};
    row.eachCell((cell, colNumber) => {
      // Convert column number to letter (A, B, C...)
      const colLetter = worksheet.getColumn(colNumber).letter;
      let value: any = cell.value;

      // Handle hyperlink objects { text: '...', hyperlink: '...' }
      if (value && typeof value === 'object' && 'text' in value) {
        value = value.text;
      }

      // Preserve native Date objects, but convert all other types to their formatted string value (cell.text)
      // This ensures things like phone numbers and postal codes are imported as strings, not numbers.
      if (!(value instanceof Date) && value !== null && value !== undefined) {
        value = cell.text;
      }

      spreadsheet[rowNumber][colLetter] = value ?? '';
    });
  });

  return spreadsheet;
}
